import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BookOpen,
  Bookmark,
  CalendarClock,
  Check,
  Clock,
  Copy,
  FlaskConical,
  GraduationCap,
  Hourglass,
  Link,
  LucideAngularModule,
  Pencil,
} from 'lucide-angular';
import { AssignmentResponse } from '../../../features/teacher/assignment/services/assignment-api.service';
import { RelativeTimePipe } from '../../pipes/relative-time.pipe';

@Component({
  selector: 'app-assignment-info-card',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, RelativeTimePipe],
  templateUrl: './assignment-info-card.html',
  styleUrl: './assignment-info-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentInfoCardComponent {
  @Input({ required: true }) assignment!: AssignmentResponse;
  @Input() tier1Name: string = '';
  @Input() showTitle: boolean = true;
  @Input() showCreatedAt: boolean = false;
  @Output() testAsStudent = new EventEmitter<void>();

  protected readonly icons = {
    BookOpen,
    Bookmark,
    CalendarClock,
    Check,
    Clock,
    Copy,
    FlaskConical,
    GraduationCap,
    Hourglass,
    Link,
    Pencil,
  };

  protected readonly copied = signal(false);

  protected get title(): string {
    return (
      this.assignment.title?.trim() ||
      this.assignment.objective?.trim() ||
      'Untitled assignment'
    );
  }

  protected get isPublished(): boolean {
    return this.assignment.status === 'published';
  }

  protected get dueDateLabel(): string {
    if (!this.assignment.dueDate) return 'No due date';
    const now = new Date();
    const due = new Date(this.assignment.dueDate);
    const diffMs = due.getTime() - now.getTime();

    if (diffMs < 0) {
      return `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }

    const diffMin = Math.floor(diffMs / 60_000);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 60)
      return `${diffMin} min${diffMin === 1 ? '' : 's'} left`;
    if (diffHour < 24)
      return `${diffHour} hour${diffHour === 1 ? '' : 's'} left`;
    if (diffDay === 1) return '1 day left';
    return `${diffDay} days left`;
  }

  protected get timeLimitLabel(): string {
    if (!this.assignment.timeLimit) return 'No time limit';
    const minutes = this.assignment.timeLimit;
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }

  protected get shareLink(): string {
    if (!this.assignment.shareCode) return '';
    const baseUrl =
      typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/a/${this.assignment.shareCode}`;
  }

  protected onTestAsStudent(): void {
    this.testAsStudent.emit();
  }

  protected async copyShareLink(): Promise<void> {
    const link = this.shareLink;
    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      // Fallback: ignore silently
    }
  }
}
